import { useState } from 'react';

import { ConfirmationModal } from '@zg-rentals/rental-revenue-components';
import billingUtils from '../utils/billingUtils';
import { Alert, Box, Spinner, Text, TextButton } from '@zillow/constellation-10';
import { useHistory, useParams } from 'react-router-dom';
import { LoadingConstants, useRentalManagerUser, useRmwPaths } from '@zg-rentals/rental-manager-env';
import type { InvoiceIdParams } from '../model/ParamTypes';
import { useAppMaintenance } from '../hooks/useMaintenance';
import { useAccountPaymentMethods } from '../hooks/useAccountPaymentMethods';
import { AdproductsUserBillingInvoicePaymentApiHandler_removeAutopay } from '@zg-rentals/ts-adproducts-client';
import { emitRemoveAutopayEvent } from '../model/ClickstreamEvents';

export type AutopayStatusWidgetProps = {
  accountNumber: string;
  disabled: boolean;
  hideSetup?: boolean;
};

const AutopayStatusWidget = ({ accountNumber, disabled, hideSetup = false }: AutopayStatusWidgetProps) => {
  const { isMaintenanceMode } = useAppMaintenance();
  const paths = useRmwPaths();
  const { invoiceId } = useParams<InvoiceIdParams>();
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
  const [cancelAutopayError, setCancelAutopayError] = useState(false);
  const { setPaymentMethodLock, paymentMethodState, getAccountPaymentMethods } = useAccountPaymentMethods();
  const { isAutopayEnabled, loadingState, paymentMethodLock } = paymentMethodState;
  const history = useHistory();
  const { user } = useRentalManagerUser();

  const isLoading = loadingState === LoadingConstants.LOADING || paymentMethodLock;

  const cancelAutoPay = () => {
    setDisplayConfirmationModal(false);
    setCancelAutopayError(false);
    setPaymentMethodLock(true);
    emitRemoveAutopayEvent({ user });
    AdproductsUserBillingInvoicePaymentApiHandler_removeAutopay({ accountNumber })
      .then(() => {
        setPaymentMethodLock(false);
        return getAccountPaymentMethods(accountNumber);
      })
      .catch(() => {
        setPaymentMethodLock(false);
        setCancelAutopayError(true);
      });
  };

  const handleCancelAutoPay = () => setDisplayConfirmationModal(true);

  const handleEditClick = () => {
    const url = `${paths.billingPage}/account/${accountNumber}`;
    const target = `${url}?prev=invoicePayment&invoiceNumber=${invoiceId}`;
    history.push(target);
  };

  const nextPaymentDate = billingUtils.getNextAutoPaymentDate();

  const renderAutopayOn = () => (
    <Box>
      <Box css={{ marginBottom: 'xs' }}>
        <Text textStyle="body-sm" css={{ fontWeight: 'semibold' }}>{`Next Payment Date: ${nextPaymentDate}`}</Text>
      </Box>
      <Text css={{ marginBottom: 'sm' }}>
        {`We will make payments to this account automatically on the next payment date.
                 Any changes for the current month must be made by ${nextPaymentDate} to avoid auto payment.`}
      </Text>
      <Box css={{ marginBlock: 'sm' }}>
        {!disabled && (
          <TextButton disabled={paymentMethodLock} onClick={handleCancelAutoPay}>
            Turn Autopay Off
          </TextButton>
        )}
      </Box>
    </Box>
  );

  const renderAutopayOff = () => (
    <Box>
      <Text css={{ marginBottom: 'sm' }}>
        Turn on autopay after adding a payment method that can be associated with it.
      </Text>
      <Box css={{ marginBlock: 'sm' }}>
        {!disabled && !hideSetup && (
          <TextButton onClick={handleEditClick} disabled={isMaintenanceMode}>
            Set up autopay
          </TextButton>
        )}
      </Box>
    </Box>
  );

  return (
    <Box css={{ marginBlock: 'md' }}>
      {cancelAutopayError && (
        <Alert tone="critical" css={{ marginBottom: 'md' }} role="alert">
          <Alert.Body>Failed to cancel autopay. Please try again.</Alert.Body>
        </Alert>
      )}
      <Box css={{ marginBottom: 'sm', display: 'flex', alignItems: 'center', gap: 'sm' }}>
        <Box
          css={{
            height: '12px',
            width: '12px',
            backgroundColor: isAutopayEnabled ? 'green' : '#bbb',
            borderRadius: '50%',
            display: 'inline-block',
          }}
        />
        <Text css={{ fontWeight: 'bold' }}>Automatic payments is {isAutopayEnabled ? 'ON' : 'OFF'}</Text>
        {isLoading && <Spinner />}
      </Box>
      {isAutopayEnabled ? renderAutopayOn() : renderAutopayOff()}
      {displayConfirmationModal && (
        <ConfirmationModal
          header="Are you sure you want to cancel auto pay?"
          submitButtonTitle="Keep Auto Pay"
          cancelButtonTitle="Cancel Auto Pay"
          textContent={
            <Text>
              Auto pay lets you avoid the risk of missed payments. If you cancel, you&apos;ll have to pay manually each
              month.
            </Text>
          }
          onCancel={cancelAutoPay}
          onSubmit={() => setDisplayConfirmationModal(false)}
        />
      )}
    </Box>
  );
};

export default AutopayStatusWidget;
